import { Dialog, DialogProps } from './Dialog';

import { Box, DialogProps as MuiDialogProps, SxProps, Typography } from '@mui/material';
import { Checkbox } from '@components/checkbox/Checkbox';
import React, { useEffect, useState } from 'react';
import { FormControlLabel } from '@components/formControlLabel/FormControlLabel';

// The new and improved ConfirmationDialog component, will replace the ConfirmationDialog
interface ConfirmationDialogProps extends Partial<MuiDialogProps> {
  /**
   * The checkbox items to be display for acknowledgement
   * cannot be used if children is provided
   */
  checkboxText?: string | string[];
  /**
   * The description of the dialog.
   * cannot be used if children is provided
   */
  description?: string;
  /**
   * The secondary description of the dialog.
   * cannot be used if children is provided
   */
  secondaryDescription?: string;
  /** really 'is thinking', should the loading spinner show? */
  isLoading?: boolean;
  isOpen: boolean;
  onCancel?: () => void;
  title?: string;

  dialogPadding?: string;
  showBorders?: boolean;
  sxProps?: DialogProps['sxProps'] & { checkboxListContainer?: SxProps };
  buttonConfig: {
    /** The alignment of the buttons in the dialog.
     * @default center
     */
    alignment?: 'right' | 'center';
    confirmText: string;
    onConfirm: () => void;
    isConfirmButtonDisabled?: boolean;
    secondaryText: string;
    isSecondaryButtonDisabled?: boolean;
    /** used if for some reason the 'x' button should be overriden */
    onSecondaryButtonClick?: () => void;
  };
  /**
   * The secondary description of the dialog.
   * will override description, secondaryDescription, and checkboxText
   */
  children?: React.ReactNode;
}

export const ConfirmationDialog = ({
  description,
  secondaryDescription,
  isLoading = false,
  isOpen,
  onCancel,
  title,
  dialogPadding,
  sxProps,
  checkboxText,
  buttonConfig,
  children,
  showBorders,
  ...other
}: ConfirmationDialogProps) => {
  const [confirmation, setConfirmation] = useState<boolean[]>(
    Array.isArray(checkboxText) ? checkboxText.map(() => false) : [!checkboxText],
  );

  const isValid = confirmation.every((value) => value);
  const checkboxItems = Array.isArray(checkboxText) ? checkboxText : checkboxText ? [checkboxText] : null;

  useEffect(() => {
    setConfirmation(Array.isArray(checkboxText) ? checkboxText.map(() => false) : [!checkboxText]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Dialog
      sx={{ '.MuiPaper-root': { padding: dialogPadding ?? '20px' } }}
      title={title}
      open={isOpen}
      showBorders={showBorders ?? false}
      isCloseButtonDisabled={buttonConfig.isSecondaryButtonDisabled}
      type='confirmation'
      handleCloseDialog={onCancel}
      primaryBtnText={buttonConfig.confirmText}
      isPrimaryBtnDisabled={buttonConfig.isConfirmButtonDisabled || !isValid}
      primaryBtnAction={buttonConfig.onConfirm}
      isPrimaryBtnLoading={isLoading}
      secondaryBtnText={buttonConfig.secondaryText}
      secondaryBtnAction={buttonConfig.onSecondaryButtonClick ?? onCancel}
      isSecondaryBtnDisabled={isLoading}
      sxProps={
        buttonConfig.alignment === 'right'
          ? {
              ...sxProps,
              dialogActionsSx: {
                justifyContent: 'flex-end',
                marginRight: 2,
                ...sxProps?.dialogActionsSx,
              },
            }
          : sxProps
      }
      {...other}
    >
      {children ? (
        children
      ) : (
        <>
          {description && <Typography>{description}</Typography>}
          {secondaryDescription && (
            <Typography
              variant='bodySmall'
              display='block'
              sx={{
                color: 'text.main',
                mt: 2,
              }}
            >
              {secondaryDescription}
            </Typography>
          )}
        </>
      )}
      {checkboxItems && checkboxItems?.length > 0 && (
        <Box sx={{ mt: 2, ...sxProps?.checkboxListContainer }}>
          {checkboxItems.map((item, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', scrollSnapMarginBottom: '2px' }}
            >
              <FormControlLabel
                label={
                  <Typography
                    variant='bodySmall'
                    sx={{
                      color: 'text.main',
                    }}
                  >
                    {item}
                  </Typography>
                }
                sx={{ fontSize: 0 }}
                control={
                  <Checkbox
                    data-testid={`checkbox-field-${index}`}
                    onChange={(event) => {
                      const newConfirmation = [...confirmation];
                      newConfirmation[index] = event.target.checked;
                      setConfirmation(newConfirmation);
                    }}
                    sx={{ paddingLeft: 0 }}
                  />
                }
              />
            </Box>
          ))}
        </Box>
      )}
    </Dialog>
  );
};
